import React, { ReactElement, useMemo, useState } from 'react';
import { ILobbyTournamentRankingsState, IRankedTournament, ITournamentStanding } from '../../../../Core/State/ILobbyTournamentRankingsState';
import { IUserState } from '../../../../Core/State/IUserState';
import {
    Paper, Typography, Box, Grid, Card, CardContent, CardHeader, Stack, Tooltip,
    ToggleButton, ToggleButtonGroup, IconButton
} from '@mui/material';
import { format } from 'date-fns';
import styles from '../../../../assets/css/components/Lobby.module.css';
import InfoIcon from '@mui/icons-material/Info';
import { t } from '../../../../i18n';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import PauseIcon from '@mui/icons-material/Pause';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import TableStructureModal from './TableStructureModal';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList, ListChildComponentProps, areEqual } from 'react-window';

interface DesktopTournamentRankingsLobbyProps {
    lobbyState: ILobbyTournamentRankingsState;
    userState: IUserState;
}

const ONLY_SHOW_VALIDATION_ON_COMPLETE = true;

const formatCurrency = (amount: number | null | undefined) => {
    if (amount === null || amount === undefined) return 'N/A';
    return `$${amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

const statusStyles: Record<string, { bg: string; color: string; icon: ReactElement }> = {
    scheduled: { bg: '#1976d233', color: '#1976d2', icon: <EventAvailableIcon sx={{ color: '#1976d2' }} /> }, // blue
    registration: { bg: '#EFC21633', color: '#EFC216', icon: <HowToRegIcon sx={{ color: '#EFC216' }} /> }, // yellow
    starting: { bg: '#64dfdf33', color: '#64dfdf', icon: <AccessTimeIcon sx={{ color: '#64dfdf' }} /> }, // cyan
    running: { bg: '#1BE95833', color: '#1BE958', icon: <AccessTimeIcon sx={{ color: '#1BE958' }} /> }, // green
    paused: { bg: '#ff980033', color: '#ff9800', icon: <InfoIcon sx={{ color: '#ff9800' }} /> }, // orange
    completed: { bg: '#16EFC133', color: '#16EFC1', icon: <EmojiEventsIcon sx={{ color: '#16EFC1' }} /> }, // teal
    cancelled: { bg: '#B3B3B333', color: '#B3B3B3', icon: <InfoIcon sx={{ color: '#B3B3B3' }} /> }, // gray
    default: { bg: '#88888833', color: '#888', icon: <InfoIcon sx={{ color: '#888' }} /> },
};

const getStatusChip = (status: IRankedTournament['status']) => {
    const key = typeof status === 'string' ? status.toLowerCase() : 'default';
    const style = statusStyles[key] || statusStyles.default;
    const displayStatus = key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ');
    return (
        <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                bgcolor: style.bg,
                color: style.color,
                fontWeight: 600,
                fontSize: '0.65rem',
                borderRadius: '3px',
                px: 0.8,
                py: 0.25,
                textTransform: 'uppercase',
                letterSpacing: '0.3px',
                border: `1px solid ${style.color}40`,
            }}
        >
            <Box sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: style.color,
                boxShadow: `0 0 4px ${style.color}`
            }} />
            {displayStatus}
        </Box>
    );
};

const STANDINGS_MAX_HEIGHT = 180;
const STANDINGS_HEADER_HEIGHT = 26;
const STANDINGS_ROW_HEIGHT = 24;
const STANDINGS_BODY_MAX_HEIGHT = STANDINGS_MAX_HEIGHT - STANDINGS_HEADER_HEIGHT;
const STANDINGS_GRID_TEMPLATE = '30px minmax(0, 0.7fr) 65px 75px 80px 75px';

type StandingsRowData = {
    rankings: ITournamentStanding[];
    userId: string;
};

const formatStandingValue = (value: number | null | undefined) => value != null ? value.toLocaleString() : '-';

const formatStandingTime = (timestamp: string | null) => {
    if (!timestamp) return '-';
    const parsedDate = new Date(timestamp);
    if (Number.isNaN(parsedDate.getTime())) return '-';
    return format(parsedDate, 'MM/dd HH:mm');
};

const StandingsRow = React.memo(({ index, data, style }: ListChildComponentProps<StandingsRowData>) => {
    const standing = data.rankings[index];
    if (!standing) return null;

    const isCurrentUser = standing.userId === data.userId;
    const rank = standing.position || standing.rank || index + 1;
    const isEliminated = standing.eliminationTime !== null;

    return (
        <Box
            component="div"
            style={style}
            sx={{
                display: 'grid',
                gridTemplateColumns: STANDINGS_GRID_TEMPLATE,
                alignItems: 'center',
                px: 1,
                columnGap: 0.5,
                fontSize: '0.65rem',
                bgcolor: isCurrentUser ? 'rgba(46, 111, 107, 0.5)' : index % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent',
                borderLeft: isCurrentUser ? '2px solid #4fd1c5' : '2px solid transparent',
                '&:hover': {
                    bgcolor: 'rgba(39, 141, 148, 0.3)',
                },
            }}
        >
            <Typography component="span" sx={{
                color: rank <= 3 ? '#4fd1c5' : '#aaa',
                fontSize: '0.65rem',
                textAlign: 'center',
                fontWeight: rank <= 3 ? 600 : 400
            }}>
                {rank}
            </Typography>
            <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                <Tooltip title={standing.username} placement="top" arrow>
                    <Typography
                        component="span"
                        noWrap
                        sx={{
                            color: isEliminated ? '#666' : '#fff',
                            fontSize: '0.65rem',
                            textDecoration: isEliminated ? 'line-through' : 'none',
                            cursor: 'help',
                            display: 'block'
                        }}
                    >
                        {standing.username}
                    </Typography>
                </Tooltip>
            </Box>
            <Typography component="span" sx={{
                color: isEliminated ? '#666' : '#4fd1c5',
                textAlign: 'right',
                fontSize: '0.65rem',
                fontWeight: 500
            }}>
                {formatStandingValue(standing.balance)}
            </Typography>
            <Typography component="span" sx={{ color: '#888', textAlign: 'right', fontSize: '0.6rem' }}>
                {formatStandingTime(standing.registrationTime)}
            </Typography>
            <Typography component="span" sx={{ color: '#888', textAlign: 'right', fontSize: '0.65rem' }}>
                {formatStandingValue(standing.chipsBeforeElimination)}
            </Typography>
            <Typography component="span" sx={{ color: '#888', textAlign: 'right', fontSize: '0.6rem' }}>
                {formatStandingTime(standing.eliminationTime)}
            </Typography>
        </Box>
    );
}, areEqual);
StandingsRow.displayName = 'StandingsRow';

const standingsRowKey = (index: number, data: StandingsRowData) => data.rankings[index]?.userId ?? index;

const StandingsTable: React.FC<{ rankings: ITournamentStanding[], userId: string }> = React.memo(({ rankings, userId }) => {
    // Sort rankings by position or rank
    const sortedRankings = useMemo(() => {
        return [...rankings].sort((a, b) => {
            const rankA = a.position || a.rank || 9999;
            const rankB = b.position || b.rank || 9999;
            return rankA - rankB;
        });
    }, [rankings]);

    const bodyHeight = Math.min(
        STANDINGS_BODY_MAX_HEIGHT,
        Math.max(sortedRankings.length * STANDINGS_ROW_HEIGHT, STANDINGS_ROW_HEIGHT)
    );
    const itemData = useMemo(() => ({ rankings: sortedRankings, userId }), [sortedRankings, userId]);

    return (
        <Paper
            elevation={0}
            sx={{
                maxHeight: STANDINGS_MAX_HEIGHT,
                mt: 0.5,
                border: '1px solid',
                borderColor: 'rgba(79, 209, 197, 0.2)',
                bgcolor: 'rgba(35, 35, 41, 0.8)',
                borderRadius: 1,
                overflow: 'hidden',
                overflowX: 'hidden'
            }}
        >
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: STANDINGS_GRID_TEMPLATE,
                    bgcolor: 'rgba(46, 111, 107, 0.3)',
                    color: '#4fd1c5',
                    fontWeight: 600,
                    borderBottom: '1px solid',
                    borderColor: 'rgba(79, 209, 197, 0.2)',
                    px: 1,
                    py: 0.5,
                    columnGap: 0.5,
                    fontSize: '0.6rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.3px',
                }}
            >
                <Typography component="span" sx={{ fontSize: '0.6rem', textAlign: 'center' }}>#</Typography>
                <Typography component="span" sx={{ fontSize: '0.6rem' }}>{t('Tournament:players.player')}</Typography>
                <Typography component="span" sx={{ textAlign: 'right', fontSize: '0.6rem' }}>{t('Tournament:players.chips')}</Typography>
                <Typography component="span" sx={{ textAlign: 'right', fontSize: '0.6rem' }}>Reg.</Typography>
                <Typography component="span" sx={{ textAlign: 'right', fontSize: '0.6rem' }}>Chips B.E.</Typography>
                <Typography component="span" sx={{ textAlign: 'right', fontSize: '0.6rem' }}>Elim.</Typography>
            </Box>
            <Box sx={{ height: bodyHeight, width: '100%', overflowX: 'hidden' }}>
                <AutoSizer>
                    {({ height, width }) => (
                        <FixedSizeList
                            height={height}
                            width={width}
                            itemSize={STANDINGS_ROW_HEIGHT}
                            itemCount={sortedRankings.length}
                            itemData={itemData}
                            overscanCount={4}
                            itemKey={standingsRowKey}
                            style={{ overflowX: 'hidden' }}
                        >
                            {StandingsRow}
                        </FixedSizeList>
                    )}
                </AutoSizer>
            </Box>
        </Paper>
    );
});
StandingsTable.displayName = 'StandingsTable';

// Clean info row component with label and value
const InfoRow: React.FC<{ label: string; value: React.ReactNode; color?: string; tooltip?: React.ReactNode }> = React.memo(({ label, value, color, tooltip }) => {
    const content = (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.3 }}>
            <Typography sx={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {label}
            </Typography>
            <Typography component="span" sx={{ fontSize: '0.75rem', color: color || '#fff', fontWeight: 500 }}>
                {value}
            </Typography>
        </Box>
    );

    if (tooltip) {
        return <Tooltip title={tooltip} placement="top" arrow><Box sx={{ cursor: 'help' }}>{content}</Box></Tooltip>;
    }
    return content;
});

const getStatusOptions = () => [
    { value: 'all', label: t('Tournament:filter.all'), icon: <InfoIcon sx={{ fontSize: '1rem', mr: 0.5 }} /> },
    { value: 'scheduled', label: t('Tournament:status.scheduled'), icon: <EventAvailableIcon sx={{ fontSize: '1rem', mr: 0.5 }} /> },
    { value: 'registration', label: t('Tournament:status.registration'), icon: <HowToRegIcon sx={{ fontSize: '1rem', mr: 0.5 }} /> },
    { value: 'starting', label: t('Tournament:filter.starting'), icon: <AccessTimeIcon sx={{ fontSize: '1rem', mr: 0.5 }} /> },
    { value: 'running', label: t('Tournament:status.running'), icon: <PlayArrowIcon sx={{ fontSize: '1rem', mr: 0.5 }} /> },
    { value: 'paused', label: t('Tournament:filter.paused'), icon: <PauseIcon sx={{ fontSize: '1rem', mr: 0.5 }} /> },
    { value: 'completed', label: t('Tournament:filter.completed'), icon: <EmojiEventsIcon sx={{ fontSize: '1rem', mr: 0.5 }} /> },
    { value: 'cancelled', label: t('Tournament:filter.cancelled'), icon: <StopIcon sx={{ fontSize: '1rem', mr: 0.5 }} /> },
];

const TournamentFilter: React.FC<{ value: string; onChange: (value: string) => void }> = ({ value, onChange }) => (
    <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        mb: 0.5,
        backgroundColor: '#232329',
        p: 0.5,
        borderRadius: 1,
        border: '1px solid',
        borderColor: '#2e6f6b',
        flexWrap: 'wrap',
    }}>
        <ToggleButtonGroup
            value={value}
            exclusive
            onChange={(_, newValue) => newValue && onChange(newValue)}
            size="small"
            sx={{
                flexWrap: 'wrap',
                '& .MuiToggleButton-root': {
                    color: '#fff',
                    borderColor: '#2e6f6b',
                    '&.Mui-selected': {
                        backgroundColor: '#2e6f6b',
                        color: '#fff',
                        '&:hover': {
                            backgroundColor: '#278d94',
                        },
                    },
                    '&:hover': {
                        backgroundColor: '#278d9433',
                    },
                    fontSize: '0.75rem',
                    py: 0.5,
                },
            }}
        >
            {getStatusOptions().map(opt => (
                <ToggleButton key={opt.value} value={opt.value}>
                    {opt.icon}
                    {opt.label}
                </ToggleButton>
            ))}
        </ToggleButtonGroup>
    </Box>
);

const DesktopTournamentRankingsLobby: React.FC<DesktopTournamentRankingsLobbyProps> = ({ lobbyState, userState }) => {
    const [filter, setFilter] = useState('all');
    const [selectedTournamentId, setSelectedTournamentId] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Get fresh tournament data from lobbyState (not a stale snapshot)
    const selectedTournament = useMemo(() => {
        if (!selectedTournamentId || !lobbyState?.tournaments) return null;
        return lobbyState.tournaments.find(t => t.gameId === selectedTournamentId) || null;
    }, [selectedTournamentId, lobbyState?.tournaments]);

    // Temporarily disabled useMemo for debugging - force re-render on every update
    const filteredTournaments = (() => {
        if (!lobbyState?.tournaments) return [];

        // If specific filter is selected, apply it
        const baseFiltered = filter === 'all'
            ? lobbyState.tournaments
            : lobbyState.tournaments.filter(tournament => tournament.status.toLowerCase() === filter);

        // For 'all' filter, limit to improve performance
        if (filter === 'all') {
            // Categorize tournaments
            const ended = baseFiltered.filter(t => ['completed', 'cancelled'].includes(t.status.toLowerCase()));
            const ongoing = baseFiltered.filter(t => ['running', 'late_registration', 'paused', 'starting'].includes(t.status.toLowerCase()));
            const upcoming = baseFiltered.filter(t => ['scheduled', 'registration'].includes(t.status.toLowerCase()));

            // Sort ended by start time (most recent first)
            const sortedEnded = ended.sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime()).slice(0, 10);

            // Sort upcoming by start time (soonest first)
            const sortedUpcoming = upcoming.sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()).slice(0, 10);

            // Return: all ongoing + limited ended + limited upcoming
            return [...ongoing, ...sortedEnded, ...sortedUpcoming];
        }

        return baseFiltered;
    })();

    const handleViewTableStructure = (tournament: IRankedTournament) => {
        setSelectedTournamentId(tournament.gameId);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedTournamentId(null);
    };

    // Temporarily disabled useMemo for debugging - force re-render on every update
    const tournamentCards = (
        filteredTournaments.map((tournament) => (
            <Grid item xs={12} md={6} lg={3} key={tournament.gameId}>
                <Card sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    border: '1px solid',
                    borderColor: '#222329',
                    bgcolor: '#232329',
                    minWidth: 0
                }}>
                    <CardHeader
                        title={
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Tooltip title={tournament.tournamentName} placement="top" arrow followCursor>
                                    <Typography
                                        variant="subtitle1"
                                        noWrap
                                        sx={{
                                            fontWeight: 500,
                                            color: '#fff',
                                            flex: 1,
                                            minWidth: 0,
                                            cursor: 'help'
                                        }}
                                    >
                                        {tournament.tournamentName}{tournament.isFinal ? ' (FINAL)' : ''}
                                    </Typography>
                                </Tooltip>
                                {getStatusChip(tournament.status)}
                                <Tooltip title={t('Tournament:tooltips.viewProgression')}>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleViewTableStructure(tournament)}
                                        sx={{
                                            color: '#888',
                                            p: 0.5,
                                            '&:hover': {
                                                backgroundColor: '#2e6f6b',
                                                color: '#fff',
                                            },
                                        }}
                                    >
                                        <VisibilityIcon sx={{ fontSize: '1rem' }} />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        }
                        sx={{ pb: 0.5, pt: 0.5, px: 1.5, bgcolor: '#222329', borderBottom: '1px solid', borderColor: '#2e6f6b' }}
                    />
                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 0.5, py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                        {/* Tournament Info - Two Column Layout */}
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            {/* Left Column */}
                            <Box sx={{ flex: 1, borderRight: '1px solid #333', pr: 1.5 }}>
                                <InfoRow label={t('Tournament:info.players')} value={`${tournament.playersRegistered} / ${tournament.maxPlayers}`} />
                                <InfoRow label="Start" value={`${format(new Date(tournament.startTime), 'MM/dd HH:mm')} (${tournament.handCount} hands)`} />
                                {tournament.lateRegistrationEndDate && (
                                    <InfoRow label={t('Tournament:info.lateRegEnd')} value={format(new Date(tournament.lateRegistrationEndDate), 'MM/dd HH:mm')} />
                                )}
                                <InfoRow label={t('Tournament:info.buyIn')} value={formatCurrency(tournament.buyIn)} />
                                <InfoRow
                                    label={t('Tournament:info.prizePool')}
                                    value={formatCurrency(tournament.guaranteedPrizePool !== undefined && tournament.guaranteedPrizePool > tournament.prizePool
                                        ? tournament.guaranteedPrizePool
                                        : tournament.prizePool)}
                                    color="#FFC107"
                                    tooltip={
                                        (tournament.collectedFromRegistrations !== undefined ||
                                         tournament.collectedFromRebuys !== undefined ||
                                         tournament.collectedFromAddons !== undefined) ? (
                                            <Box sx={{ p: 0.5 }}>
                                                {tournament.collectedFromRegistrations !== undefined && (
                                                    <Typography sx={{ fontSize: '11px' }}>Buy-ins: {formatCurrency(tournament.collectedFromRegistrations)}</Typography>
                                                )}
                                                {tournament.collectedFromRebuys !== undefined && tournament.collectedFromRebuys > 0 && (
                                                    <Typography sx={{ fontSize: '11px' }}>Rebuys: {formatCurrency(tournament.collectedFromRebuys)}</Typography>
                                                )}
                                                {tournament.collectedFromAddons !== undefined && tournament.collectedFromAddons > 0 && (
                                                    <Typography sx={{ fontSize: '11px' }}>Add-ons: {formatCurrency(tournament.collectedFromAddons)}</Typography>
                                                )}
                                            </Box>
                                        ) : undefined
                                    }
                                />
                            </Box>
                            {/* Right Column */}
                            <Box sx={{ flex: 1, pl: 0.5 }}>
                                {tournament.startingChips !== undefined && tournament.startingChips > 0 && (
                                    <InfoRow
                                        label={t('Tournament:info.startingStack')}
                                        value={tournament.startingChips.toLocaleString()}
                                        tooltip={<Typography sx={{ fontSize: '11px' }}>{tournament.startingChips.toLocaleString()} × {tournament.playersRegistered} = {(tournament.startingChips * tournament.playersRegistered).toLocaleString()}</Typography>}
                                    />
                                )}
                                <InfoRow label={t('Tournament:info.initialChips')} value={tournament.initialTotalChipBalance.toLocaleString()} />
                                {(() => {
                                    const rebuyCount = tournament.rebuyCount ?? 0;
                                    const addonCount = tournament.addonCount ?? 0;
                                    const hasRebuys = (tournament.collectedFromRebuys ?? 0) > 0 || rebuyCount > 0;
                                    const hasAddons = (tournament.collectedFromAddons ?? 0) > 0 || addonCount > 0;
                                    const chipsAdded = tournament.currentTotalChipBalance - tournament.initialTotalChipBalance;
                                    const hasChipsAdded = chipsAdded > 0 && (hasRebuys || hasAddons);

                                    // Use rebuyChips/addonChips if provided, otherwise default to startingChips
                                    const rebuyChipsPerUnit = tournament.rebuyChips ?? tournament.startingChips ?? 0;
                                    const addonChipsPerUnit = tournament.addonChips ?? tournament.startingChips ?? 0;

                                    const chipsFromRebuys = rebuyCount > 0 && rebuyChipsPerUnit > 0 ? rebuyChipsPerUnit * rebuyCount : 0;
                                    const chipsFromAddons = addonCount > 0 && addonChipsPerUnit > 0 ? addonChipsPerUnit * addonCount : 0;

                                    const canValidate = chipsFromRebuys > 0 || chipsFromAddons > 0;
                                    const expectedChips = tournament.initialTotalChipBalance + chipsFromRebuys + chipsFromAddons;
                                    const difference = Math.abs(expectedChips - tournament.currentTotalChipBalance);
                                    const isValid = difference <= 0.3;

                                    // Only show validation if tournament is complete/cancelled (when flag is true)
                                    const isFinished = ['completed', 'cancelled'].includes(tournament.status.toLowerCase());
                                    const showValidation = ONLY_SHOW_VALIDATION_ON_COMPLETE ? isFinished : true;

                                    return (
                                        <InfoRow
                                            label={t('Tournament:info.currentChips')}
                                            value={
                                                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    {tournament.currentTotalChipBalance.toLocaleString()}
                                                    {showValidation && hasChipsAdded && (
                                                        <span style={{ color: '#4caf50', fontSize: '0.65rem' }}>+{chipsAdded.toLocaleString()}</span>
                                                    )}
                                                    {showValidation && hasChipsAdded && canValidate && (
                                                        isValid ? <CheckCircleIcon sx={{ fontSize: '12px', color: '#4caf50' }} /> : <CancelIcon sx={{ fontSize: '12px', color: '#f44336' }} />
                                                    )}
                                                </span>
                                            }
                                            tooltip={hasChipsAdded && showValidation ? (
                                                <Box sx={{ p: 0.5 }}>
                                                    <Typography sx={{ fontSize: '11px' }}>Initial: {tournament.initialTotalChipBalance.toLocaleString()}</Typography>
                                                    {hasRebuys && chipsFromRebuys > 0 && (
                                                        <Typography sx={{ fontSize: '11px', color: '#4caf50' }}>
                                                            + Rebuys: {rebuyCount} × {rebuyChipsPerUnit.toLocaleString()} = {chipsFromRebuys.toLocaleString()}
                                                        </Typography>
                                                    )}
                                                    {hasAddons && chipsFromAddons > 0 && (
                                                        <Typography sx={{ fontSize: '11px', color: '#2196f3' }}>
                                                            + Add-ons: {addonCount} × {addonChipsPerUnit.toLocaleString()} = {chipsFromAddons.toLocaleString()}
                                                        </Typography>
                                                    )}
                                                    <Typography sx={{ fontSize: '11px', fontWeight: 600, mt: 0.5, pt: 0.5, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                                                        Expected: {expectedChips.toLocaleString()}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '11px' }}>
                                                        Actual: {tournament.currentTotalChipBalance.toLocaleString()}
                                                    </Typography>
                                                    {canValidate && (
                                                        <Typography sx={{ fontSize: '11px', color: isValid ? '#4caf50' : '#f44336', mt: 0.3 }}>
                                                            {isValid ? '✓ Verified' : `✗ Difference: ${difference.toLocaleString()}`}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            ) : undefined}
                                        />
                                    );
                                })()}
                                {/* Rebuys - only show if purchased */}
                                {['running', 'paused', 'late_registration', 'completed'].includes(tournament.status.toLowerCase()) &&
                                 tournament.collectedFromRebuys !== undefined && tournament.collectedFromRebuys > 0 && (
                                    <InfoRow
                                        label={t('Tournament:info.rebuys')}
                                        value={<span>{tournament.rebuyCount ?? 0} <span style={{ color: '#888' }}>({formatCurrency(tournament.collectedFromRebuys)})</span></span>}
                                        color="#4caf50"
                                    />
                                )}
                                {/* Addons - only show if purchased */}
                                {['running', 'paused', 'late_registration', 'completed'].includes(tournament.status.toLowerCase()) &&
                                 tournament.collectedFromAddons !== undefined && tournament.collectedFromAddons > 0 && (
                                    <InfoRow
                                        label={t('Tournament:info.addons')}
                                        value={<span>{tournament.addonCount ?? 0} <span style={{ color: '#888' }}>({formatCurrency(tournament.collectedFromAddons)})</span></span>}
                                        color="#2196f3"
                                    />
                                )}
                                                            </Box>
                        </Box>
                        {tournament.rankings && tournament.rankings.length > 0 ? (
                            <StandingsTable rankings={tournament.rankings} userId={userState.id} />
                        ) : (
                            <Typography sx={{ p: 1, fontStyle: 'italic', color: '#888', textAlign: 'center', mt: 1, fontSize: '0.75rem' }}>
                                {tournament.status === 'REGISTRATION' || tournament.status === 'SCHEDULED' ? 'Rankings available after start.' : 'No rankings yet.'}
                            </Typography>
                        )}
                    </CardContent>
                </Card>
            </Grid>
        ))
    );

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                maxHeight: 'calc(100vh - 120px)',
                height: '100%',
                position: 'relative',
                bgcolor: '#18181c',
                boxSizing: 'border-box',
            }}
        >
            <TournamentFilter value={filter} onChange={setFilter} />

            <Box
                sx={{
                    flexGrow: 1,
                    overflowY: 'auto',
                    px: 1.5,
                    pt: 0,
                    pb: 7,
                    '&::-webkit-scrollbar': {
                        width: '10px',
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: 'rgba(0, 0, 0, 0.1)',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: '#2e6f6b',
                        borderRadius: '4px',
                    },
                }}
                className={`${styles.lobbyContainer} ${styles.tournamentRankingsScroller}`}
            >
                {!lobbyState || !lobbyState.tournaments || lobbyState.tournaments.length === 0 ? (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h6" color="text.secondary">No tournament rankings available at the moment.</Typography>
                    </Box>
                ) : filteredTournaments.length > 0 ? (
                    <Grid container spacing={1.5} sx={{ m: 0, width: '100%' }}>
                        {tournamentCards}
                    </Grid>
                ) : (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="body1" sx={{ color: '#888' }}>
                            No {filter === 'all' ? '' : filter} tournaments found.
                        </Typography>
                    </Box>
                )}

                <Box sx={{ height: '20px', width: '100%' }} />
            </Box>

            {/* Table Structure Modal */}
            {selectedTournament && (
                <TableStructureModal
                    open={isModalOpen}
                    onClose={handleCloseModal}
                    tournament={selectedTournament}
                />
            )}
        </Box>
    );
};

export default DesktopTournamentRankingsLobby; 
